<template>
  <!-- Initial Prompt -->
  <div
    v-if="!cropImage"
    class="relative flex flex-col w-full font-regular p-8 rounded-lg text-gray-900 dark:text-gray-200 bg-white dark:bg-gray-800 transition-colors duration-300"
  >
    <!-- Header -->
    <div
      class="flex flex-col items-start justify-start p-4 font-semibold leading-snug text-blue-gray-900 dark:text-gray-200 mb-6"
    >
      <h3
        class="block antialiased leading-snug tracking-normal font-semibold text-3xl portrait:text-2xl dark:text-gray-200"
      >
        Crop Image
      </h3>
      <p
        class="block mt-2 opacity-80 antialiased text-[18px] font-medium leading-6 text-gray-800 dark:text-gray-300 max-w-md"
      >
        Do you wish to crop image or proceed to upload?
      </p>
    </div>

    <!-- Buttons -->
    <div class="flex flex-col gap-4">
      <button
        @click="cropImage = true"
        class="align-middle select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-[24px] portrait:text-lg py-3 px-6 rounded-lg bg-button dark:bg-sub-900 text-white dark:text-gray-200 shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none"
      >
        Yes, crop image
      </button>
      <button
        @click="closeDialog"
        class="select-none rounded-lg border border-main-900 dark:border-gray-300 py-3 px-4 text-center align-middle text-[24px] portrait:text-lg font-semibold text-main-900 dark:text-gray-300 transition-all hover:opacity-75 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
      >
        No, proceed to upload
      </button>
    </div>

    <!-- Close Button -->
    <button
      @click="cancelCropper"
      class="!absolute top-3 right-3 h-12 w-12 select-none rounded-lg text-center align-middle font-medium uppercase text-gray-800 dark:text-gray-200 transition-all hover:bg-gray-800/10 dark:hover:bg-gray-200/10 active:bg-gray-800/30 dark:active:bg-gray-200/30 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
      type="button"
    >
      <span
        class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
      >
        <i class="fa-solid fa-xmark text-[40px]"></i>
      </span>
    </button>
  </div>

  <!-- Cropper Interface -->
  <div
    v-if="cropImage"
    class="relative flex flex-col w-full font-regular p-8 rounded-lg text-gray-900 dark:text-gray-200 bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 transition-colors duration-300"
  >
    <!-- Header -->
    <div
      class="flex flex-col items-start justify-start p-4 font-semibold leading-snug text-blue-gray-900 dark:text-gray-200 mb-6"
    >
      <h3
        class="block antialiased leading-snug tracking-normal font-semibold text-3xl portrait:text-2xl dark:text-gray-200"
      >
        Crop Image
      </h3>
    </div>

    <!-- Cropper Container -->
    <div class="flex w-full h-full max-h-[calc(100vh-230px)] pb-8">
      <vue-cropper
        ref="cropper"
        :src="imageUrl"
        :aspectRatio="aspectRatio"
        :viewMode="viewMode"
        @crop="onCrop"
        :zoomable="true"
        :cropBoxMovable="true"
        :minContainerWidth="100"
        :minContainerHeight="100"
        :autoCrop="true"
        :autoCropArea="1"
        :background="true"
        :guides="true"
        :dragMode="'move'"
        :responsive="true"
        :restore="true"
        :cropBoxResizable="true"
        :zoomOnWheel="true"
        :scalable="true"
        :modal="true"
        :center="true"
      />
    </div>

    <!-- Action Buttons -->
    <div class="flex flex-row justify-end items-center gap-4">
      <button
        @click="saveCroppedImage"
        class="align-middle select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-[24px] portrait:text-lg py-3 px-6 rounded-lg bg-button dark:bg-sub-800 text-white dark:text-gray-200 shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none"
      >
        Save
      </button>
      <button
        @click="closeDialog"
        class="select-none rounded-lg border border-main-900 dark:border-gray-300 py-3 px-4 text-center align-middle text-[24px] portrait:text-lg font-semibold text-main-900 dark:text-gray-300 transition-all hover:opacity-75 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
      >
        Cancel
      </button>
    </div>

    <!-- Close Button -->
    <button
      @click="cancelCropper"
      class="!absolute top-3 right-3 h-12 w-12 select-none rounded-lg text-center align-middle font-medium uppercase text-gray-800 dark:text-gray-200 transition-all hover:bg-gray-800/10 dark:hover:bg-gray-200/10 active:bg-gray-800/30 dark:active:bg-gray-200/30 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
      type="button"
    >
      <span
        class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
      >
        <i class="fa-solid fa-xmark text-[40px]"></i>
      </span>
    </button>
  </div>
</template>

<script>
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";

export default {
  name: "CropperComponent",
  components: {
    VueCropper,
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    imageUrl: {
      type: String,
      required: true,
    },
    aspectRatio: {
      type: Number,
      default: 1,
    },
    viewMode: {
      type: Number,
      default: 1,
    },
  },
  emits: ["crop", "cropped", "close-dialog"],
  data() {
    return {
      cropImage: false,
    };
  },
  mounted() {
    window.addEventListener("keydown", this.handleEscapeKey);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleEscapeKey);
  },
  methods: {
    handleEscapeKey(event) {
      if (event.key === "Escape" && this.cropImage) {
        this.closeDialog();
      }
    },
    onCrop(event) {
      this.$emit("crop", event.details);
    },
    saveCroppedImage() {
      const croppedCanvas = this.$refs.cropper.getCroppedCanvas();
      this.$emit("cropped", croppedCanvas);
      this.$refs.cropper.destroy();
      this.cropImage = false;
    },
    cancelCropper() {
      if (this.$refs.cropper) {
        this.$refs.cropper.destroy();
      }
      this.cropImage = false;
      this.$emit("cancel");
    },
    closeDialog() {
      if (this.$refs.cropper) {
        this.$refs.cropper.destroy();
      }
      this.cropImage = false;
      this.$emit("close-dialog");
    },
  },
};
</script>
